import React, { useState, useEffect, useRef } from "react";
import { Eye, Download, Upload } from "lucide-react";
import ProductService from "../../../services/api/admin.product.service";

import ProductHistory from "./ProductHistory";
const ProductList = () => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "uniqueTitle",
    direction: "ascending",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUniqueTitle, setSelectedUniqueTitle] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [filters, setFilters] = useState({
    sector: "",
    industry: "",
  });
  const fetchStocks = async () => {
    setLoading(true);
    try {
      // Use ProductService to fetch products
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        sector: filters.sector || undefined,
        industry: filters.industry || undefined,
        searchTerm: searchTerm || undefined,
      };

      const result = await ProductService.getAllProducts(params);

      if (result.success) {
        // Transform API data to match our component's expected format
        const transformedStocks = result.data.map((item) => ({
          id: item._id,
          symbol: item.basicData?.uniqueTitle || "",
          name: item.basicData?.legalName || "",
          buyPrice: item.weekData52?.buyPrice || 0,
          sellPrice: item.weekData52?.sellPrice || 0,
          high52Week: item.weekData52?.high52Week || 0,
          low52Week: item.weekData52?.low52Week || 0,
          lastUpdate: item.weekData52?.lastUpdate
            ? new Date(item.weekData52.lastUpdate)
            : null,
          sector: item.basicData?.sector || "",
          industry: item.basicData?.industry || "",
          listingStatus: item.basicData?.listingStatus || "",
          logo: item.logo || item.basicData?.logo || null,
        }));

        setStocks(transformedStocks);
        setPagination(result.pagination);
      } else {
        throw new Error("Data fetch failed");
      }
    } catch (error) {
      console.error("Error fetching stocks:", error);
    } finally {
      setLoading(false);
    }
  };
  // Fetch data from API using ProductService
  useEffect(() => {
    fetchStocks();
  }, [pagination.currentPage, pagination.itemsPerPage, filters, searchTerm]);

  // Handle CSV import using ProductService
  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportLoading(true);
    setImportResult(null);

    try {
      // Use ProductService to import CSV
      const result = await ProductService.importProductsCSV(file);
      fetchStocks();
      if (result.success) {
        setImportResult({
          success: true,
          message: "Excel import completed successfully",
          details: result.summary,
        });

        // Refresh the data to show newly imported stocks
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      } else {
        setImportResult({
          success: false,
          message: result.message || "Failed to import CSV",
          details: result.errors,
        });
      }
    } catch (error) {
      console.error("Error importing CSV:", error);
      setImportResult({
        success: false,
        message: "Error importing CSV",
        details: error.message,
      });
    } finally {
      setImportLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Trigger file input click
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle CSV export using ProductService
  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      // Use ProductService to export CSV
      const blob = await ProductService.exportProductsCSV();

      // Create a link element to download the file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = "InvestmentPlatformProducts.xlsx";

      // Append to the document and trigger the download
      document.body.appendChild(a);
      a.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      alert("Failed to export data. Please try again later.");
    } finally {
      setExportLoading(false);
    }
  };

  // Handle view details click - will use ProductService for product history
  // Handle view details click
  const handleViewDetails = (uniqueTitle) => {
    setSelectedUniqueTitle(uniqueTitle);
    setShowHistory(true);
  };
  // Handle back to dashboard from product history
  const handleBackToDashboard = () => {
    setSelectedUniqueTitle(null);
    setShowHistory(false);
  };

  // Sorting function
  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting to stocks
  const sortedStocks = React.useMemo(() => {
    let sortableStocks = [...stocks];
    if (sortConfig.key) {
      sortableStocks.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableStocks;
  }, [stocks, sortConfig]);

  // Filter stocks by search term - FIX HERE
  const filteredStocks = sortedStocks.filter(
    (stock) =>
      (stock.symbol?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (stock.name?.toLowerCase() || "").includes(searchTerm.toLowerCase())
  );

  // Format large numbers with commas
  const formatNumber = (num) => {
    return num.toLocaleString("en-IN");
  };

  // Format currency in INR
  const formatCurrency = (num) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(num);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setPagination({ ...pagination, currentPage: newPage });
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Reset to first page when filters change
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // Calculate price change percentage between high and low 52 week
  const calculatePriceRange = (high, low) => {
    if (!high || !low || high === 0) return 0;
    return ((high - low) / low) * 100;
  };

  return (
    <div className="p-3">
      {showHistory ? (
        <ProductHistory
          uniqueTitle={selectedUniqueTitle}
          onBackClick={handleBackToDashboard}
        />
      ) : (
        <>
          <div className="flex flex-wrap justify-between items-center mb-3 gap-2">
            <h1 className="text-lg font-bold">Indian Stock Market</h1>

            <div className="flex space-x-2">
              {/* Import CSV Button */}
              <div className="relative">
                <input
                  type="file"
                  accept=".xlsx"
                  ref={fileInputRef}
                  onChange={handleImportCSV}
                  className="hidden"
                />
                <button
                  onClick={triggerFileInput}
                  disabled={importLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium py-1.5 px-3 rounded flex items-center transition-colors"
                >
                  <Upload className="h-3 w-3 sm:mr-1" />
                  <span className="hidden sm:inline">
                    {importLoading ? "Importing..." : "Import Excel"}
                  </span>
                </button>
              </div>

              {/* Export CSV Button */}
              <button
                onClick={handleExportCSV}
                disabled={exportLoading}
                className="bg-green-600 hover:bg-green-700 text-white text-xs font-medium py-1.5 px-3 rounded flex items-center transition-colors"
              >
                <Download className="h-3 w-3 sm:mr-1" />
                <span className="hidden sm:inline">
                  {exportLoading ? "Exporting..." : "Export Excel"}
                </span>
              </button>
            </div>
          </div>

          {/* Import Result Message */}
          {importResult && (
            <div
              className={`p-3 mb-3 rounded text-xs ${importResult.success
                  ? "bg-green-50 border border-green-200 text-green-800"
                  : "bg-red-50 border border-red-200 text-red-800"
                }`}
            >
              <div className="font-medium">{importResult.message}</div>
              {importResult.success && importResult.details && (
                <div className="mt-1">
                  <p>Total: {importResult.details.total}</p>
                  <p>Created: {importResult.details.created}</p>
                  <p>Updated: {importResult.details.updated}</p>
                  <p>Failed: {importResult.details.failed}</p>
                  <p>Skipped: {importResult.details.skipped}</p>
                </div>
              )}
              <button
                className="mt-1 underline"
                onClick={() => setImportResult(null)}
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Search and filters */}
          <div className="mb-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <input
                type="text"
                placeholder="Search stocks..."
                className="w-full p-1.5 border rounded text-xs"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div>
              <select
                name="sector"
                className="w-full p-1.5 border rounded text-xs"
                value={filters.sector}
                onChange={handleFilterChange}
              >
                <option value="">All Sectors</option>
                <option value="Refrigeration/ AC">Refrigeration/ AC</option>
                <option value="Finance">Finance</option>
                <option value="Technology">Technology</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Energy">Energy</option>
              </select>
            </div>

            <div>
              <select
                name="industry"
                className="w-full p-1.5 border rounded text-xs"
                value={filters.industry}
                onChange={handleFilterChange}
              >
                <option value="">All Industries</option>
                <option value="Manufacturing">Manufacturing</option>
                <option value="Software">Software</option>
                <option value="Banking">Banking</option>
                <option value="Pharmaceuticals">Pharmaceuticals</option>
                <option value="Oil & Gas">Oil & Gas</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-sm">Loading stocks...</div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      <th
                        className="py-1.5 px-2 text-left cursor-pointer"
                        onClick={() => requestSort("symbol")}
                      >
                        Symbol
                        {sortConfig.key === "symbol" && (
                          <span>
                            {sortConfig.direction === "ascending" ? " ↑" : " ↓"}
                          </span>
                        )}
                      </th>
                      <th
                        className="py-1.5 px-2 text-left cursor-pointer"
                        onClick={() => requestSort("name")}
                      >
                        Name
                        {sortConfig.key === "name" && (
                          <span>
                            {sortConfig.direction === "ascending" ? " ↑" : " ↓"}
                          </span>
                        )}
                      </th>
                      <th
                        className="py-1.5 px-2 text-right cursor-pointer"
                        onClick={() => requestSort("buyPrice")}
                      >
                        Buy Price (₹)
                        {sortConfig.key === "buyPrice" && (
                          <span>
                            {sortConfig.direction === "ascending" ? " ↑" : " ↓"}
                          </span>
                        )}
                      </th>
                      <th
                        className="py-1.5 px-2 text-right cursor-pointer"
                        onClick={() => requestSort("sellPrice")}
                      >
                        Sell Price (₹)
                        {sortConfig.key === "sellPrice" && (
                          <span>
                            {sortConfig.direction === "ascending" ? " ↑" : " ↓"}
                          </span>
                        )}
                      </th>
                      <th
                        className="py-1.5 px-2 text-right cursor-pointer"
                        onClick={() => requestSort("high52Week")}
                      >
                        52W High
                        {sortConfig.key === "high52Week" && (
                          <span>
                            {sortConfig.direction === "ascending" ? " ↑" : " ↓"}
                          </span>
                        )}
                      </th>
                      <th
                        className="py-1.5 px-2 text-right cursor-pointer"
                        onClick={() => requestSort("low52Week")}
                      >
                        52W Low
                        {sortConfig.key === "low52Week" && (
                          <span>
                            {sortConfig.direction === "ascending" ? " ↑" : " ↓"}
                          </span>
                        )}
                      </th>
                      <th
                        className="py-1.5 px-2 text-center cursor-pointer"
                        onClick={() => requestSort("listingStatus")}
                      >
                        Status
                        {sortConfig.key === "listingStatus" && (
                          <span>
                            {sortConfig.direction === "ascending" ? " ↑" : " ↓"}
                          </span>
                        )}
                      </th>
                      <th
                        className="py-1.5 px-2 text-center cursor-pointer"
                        onClick={() => requestSort("lastUpdate")}
                      >
                        Last Updated
                        {sortConfig.key === "lastUpdate" && (
                          <span>
                            {sortConfig.direction === "ascending" ? " ↑" : " ↓"}
                          </span>
                        )}
                      </th>
                      <th className="py-1.5 px-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStocks.length > 0 ? (
                      filteredStocks.map((stock) => {
                        const priceRangePercent = calculatePriceRange(
                          stock.high52Week,
                          stock.low52Week
                        );

                        return (
                          <tr
                            key={stock.id}
                            className="border-b hover:bg-gray-50"
                          >
                            <td className="py-1.5 px-2 font-medium">
                              <div className="flex items-center">
                                <div className="w-6 h-6 rounded-md overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center text-[10px] mr-2 flex-shrink-0">
                                  {stock.logo ? (
                                    <img
                                      src={stock.logo}
                                      alt={stock.name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  <div className={stock.logo ? "hidden" : "w-full h-full flex items-center justify-center bg-blue-100 text-blue-600 font-bold"}>
                                    {stock.name.charAt(0).toUpperCase()}
                                  </div>
                                </div>
                                <span>{stock.symbol}</span>
                              </div>
                            </td>
                            <td className="py-1.5 px-2">{stock.name}</td>
                            <td className="py-1.5 px-2 text-right">
                              {formatCurrency(stock.buyPrice)}
                            </td>
                            <td className="py-1.5 px-2 text-right">
                              {formatCurrency(stock.sellPrice)}
                            </td>
                            <td className="py-1.5 px-2 text-right text-green-600">
                              {formatCurrency(stock.high52Week)}
                            </td>
                            <td className="py-1.5 px-2 text-right text-red-600">
                              {formatCurrency(stock.low52Week)}
                            </td>
                            <td className="py-1.5 px-2 text-center">
                              <span
                                className={`px-1.5 py-0.5 rounded-full text-xs ${stock.listingStatus === "Listed"
                                    ? "bg-green-100 text-green-800"
                                    : stock.listingStatus === "DRHP Filed"
                                      ? "bg-blue-100 text-blue-800"
                                      : stock.listingStatus === "DRHP Not Filed"
                                        ? "bg-yellow-100 text-yellow-800"
                                        : "bg-gray-100 text-gray-800"
                                  }`}
                              >
                                {stock.listingStatus || "Unknown"}
                              </span>
                            </td>
                            <td className="py-1.5 px-2 text-center text-gray-600">
                              {formatDate(stock.lastUpdate)}
                            </td>
                            <td className="py-1.5 px-2 text-center">
                              <button
                                onClick={() => handleViewDetails(stock.symbol)}
                                className="bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs py-0.5 px-2 rounded flex items-center transition-colors"
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                <span className="hidden sm:inline">
                                  View Details
                                </span>
                                <span className="sm:hidden">View</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="9" className="py-3 px-2 text-center">
                          No stocks found matching "{searchTerm}"
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-3 flex flex-col sm:flex-row justify-between items-center gap-2">
                <div className="text-xs text-gray-600">
                  Showing{" "}
                  {Math.min(
                    (pagination.currentPage - 1) * pagination.itemsPerPage + 1,
                    pagination.totalItems
                  )}{" "}
                  -{" "}
                  {Math.min(
                    pagination.currentPage * pagination.itemsPerPage,
                    pagination.totalItems
                  )}{" "}
                  of {pagination.totalItems} items
                </div>
                <div className="flex space-x-1 text-xs">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className={`px-2 py-0.5 border rounded ${pagination.currentPage === 1
                        ? "text-gray-400"
                        : "hover:bg-gray-100"
                      }`}
                  >
                    Previous
                  </button>

                  {[...Array(Math.min(5, pagination.totalPages))].map(
                    (_, i) => {
                      // Calculate which page numbers to show
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (
                        pagination.currentPage >=
                        pagination.totalPages - 2
                      ) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={i}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-2 py-0.5 border rounded ${pagination.currentPage === pageNum
                              ? "bg-blue-500 text-white"
                              : "hover:bg-gray-100"
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}

                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className={`px-2 py-0.5 border rounded ${pagination.currentPage === pagination.totalPages
                        ? "text-gray-400"
                        : "hover:bg-gray-100"
                      }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ProductList;
